import { useState, useEffect, useCallback, useContext } from 'react';
import { Trash2, RefreshCw, Mail, UserPlus, Shield, User } from 'lucide-react';
import { getUsers, deleteUser } from '@/services/users.js';
import { createInvite } from '@/services/user-invite.js';
import UserContext from '../../context/users';
import { EditorRole, AdminRole, OwnerRole } from '@/constants.js';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogHeader, DialogContent, DialogFooter, ConfirmDialog } from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/cn';

const roleConfig = {
    [OwnerRole]: { label: 'Owner', variant: 'warning', icon: Shield },
    [AdminRole]: { label: 'Admin', variant: 'primary', icon: Shield },
    [EditorRole]: { label: 'Editor', variant: 'success', icon: User },
};

function UserSkeleton() {
    return (
        <div className="flex items-center gap-3 px-4 py-3">
            <div className="h-9 w-9 animate-pulse rounded-full" style={{ background: 'var(--dash-surface-hover)' }} />
            <div className="flex flex-1 flex-col gap-1.5">
                <div className="h-3.5 w-32 animate-pulse rounded-md" style={{ background: 'var(--dash-surface-hover)' }} />
                <div className="h-2.5 w-48 animate-pulse rounded-md" style={{ background: 'var(--dash-surface-hover)' }} />
            </div>
        </div>
    );
}

export default function UsersManager() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentUser] = useContext(UserContext);
    const [currentPage, setCurrentPage] = useState(1);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviting, setInviting] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const pageSize = 8;

    const loadUsers = useCallback(async () => {
        if (!currentUser?.companyID) return;
        setLoading(true);
        try {
            const { data } = await getUsers({
                page: 1,
                perPage: 100,
                companyID: currentUser.companyID,
            });
            setUsers(data);
        } catch (e) {
            console.error(e);
            toast.error('Falha ao carregar usuários');
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deleteUser(deleteTarget.id);
            toast.success('Usuário removido com sucesso');
            setDeleteTarget(null);
            loadUsers();
        } catch (e) {
            console.error(e);
            toast.error('Falha ao remover usuário');
        } finally {
            setDeleting(false);
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        if (!inviteEmail.trim()) return;
        setInviting(true);
        try {
            await createInvite(inviteEmail, currentUser.companyID);
            toast.success('Convite enviado com sucesso!');
            setInviteEmail('');
            setInviteOpen(false);
        } catch (err) {
            console.error(err);
            toast.error('Falha ao enviar convite');
        } finally {
            setInviting(false);
        }
    };

    const totalPages = Math.ceil(users.length / pageSize);
    const paginatedUsers = users.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return (
        <>
            {/* Actions bar */}
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs" style={{ color: 'var(--dash-text-muted)' }}>
                    {users.length} {users.length === 1 ? 'usuário' : 'usuários'}
                </p>
                <div className="flex gap-2">
                    <Button variant="ghost" size="sm" leftIcon={RefreshCw} onClick={loadUsers}>
                        Atualizar
                    </Button>
                    <Button variant="primary" size="sm" leftIcon={UserPlus} onClick={() => setInviteOpen(true)}>
                        Convidar
                    </Button>
                </div>
            </div>

            {/* List */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex flex-col divide-y" style={{ borderColor: 'var(--dash-border)' }}>
                            {[0, 1, 2, 3].map((i) => <UserSkeleton key={i} />)}
                        </div>
                    ) : users.length === 0 ? (
                        <EmptyState
                            icon={User}
                            title="Nenhum usuário"
                            description="Convide membros para sua empresa."
                            action={
                                <Button variant="primary" size="sm" leftIcon={UserPlus} onClick={() => setInviteOpen(true)}>
                                    Convidar Usuário
                                </Button>
                            }
                        />
                    ) : (
                        <div className="flex flex-col">
                            {paginatedUsers.map((user, index) => {
                                const role = roleConfig[user.role] || { label: user.role || 'N/A', variant: 'muted' };
                                return (
                                    <div
                                        key={user.id}
                                        className={cn(
                                            'group flex items-center gap-3 px-4 py-3 transition-colors sm:px-5',
                                            index !== paginatedUsers.length - 1 && 'border-b'
                                        )}
                                        style={{ borderColor: 'var(--dash-border)' }}
                                        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--dash-surface-hover)')}
                                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                    >
                                        {/* Avatar */}
                                        {user.profilePicture ? (
                                            <img
                                                src={user.profilePicture}
                                                alt={user.name}
                                                className="h-9 w-9 rounded-full object-cover"
                                            />
                                        ) : (
                                            <div
                                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
                                                style={{
                                                    background: 'linear-gradient(135deg, #ad34fe 0%, #7b1fd4 100%)',
                                                    color: '#fff',
                                                }}
                                            >
                                                {user.name?.charAt(0)?.toUpperCase() || '?'}
                                            </div>
                                        )}

                                        {/* Info */}
                                        <div className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-3">
                                            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                                <span className="truncate text-sm font-medium" style={{ color: 'var(--dash-text)' }}>
                                                    {user.name}
                                                </span>
                                                <span className="truncate text-xs" style={{ color: 'var(--dash-text-muted)' }}>
                                                    {user.email}
                                                </span>
                                            </div>
                                            <Badge variant={role.variant} icon={role.icon}>
                                                {role.label}
                                            </Badge>
                                        </div>

                                        {/* Delete */}
                                        <button
                                            type="button"
                                            onClick={() => setDeleteTarget(user)}
                                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg opacity-0 transition-all duration-200 group-hover:opacity-100"
                                            style={{
                                                color: 'var(--dash-text-muted)',
                                                background: 'transparent',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.color = '#d61f3f';
                                                e.currentTarget.style.background = 'rgba(254,52,86,0.08)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.color = 'var(--dash-text-muted)';
                                                e.currentTarget.style.background = 'transparent';
                                            }}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                            key={page}
                            type="button"
                            onClick={() => setCurrentPage(page)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-xs font-medium transition-all duration-200"
                            style={{
                                background: page === currentPage ? 'var(--dash-primary-soft)' : 'transparent',
                                color: page === currentPage ? 'var(--dash-primary)' : 'var(--dash-text-muted)',
                                border: page === currentPage ? '1px solid var(--dash-primary-border)' : '1px solid transparent',
                            }}
                        >
                            {page}
                        </button>
                    ))}
                </div>
            )}

            {/* Invite Dialog */}
            <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} size="sm">
                <DialogHeader
                    icon={UserPlus}
                    title="Convidar Usuário"
                    description="Envie um convite por email para adicionar um novo membro."
                />
                <form onSubmit={handleInvite}>
                    <DialogContent>
                        <Input
                            type="email"
                            leftIcon={Mail}
                            placeholder="email@exemplo.com"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            required
                        />
                    </DialogContent>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setInviteOpen(false)} disabled={inviting}>
                            Cancelar
                        </Button>
                        <Button type="submit" variant="primary" loading={inviting}>
                            Enviar Convite
                        </Button>
                    </DialogFooter>
                </form>
            </Dialog>

            {/* Delete Confirm */}
            <ConfirmDialog
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title="Remover usuário"
                description={`Tem certeza que deseja remover ${deleteTarget?.name}? Esta ação não pode ser desfeita.`}
                confirmLabel="Remover"
                loading={deleting}
            />
        </>
    );
}
